// import { SxProps, Theme } from '@mui/material/styles';
import { ErrorBoundary } from '@sentry/react';
import React, { useRef, useState } from 'react';

import { Company, isStCapacityCompany, ServiceTitanMappedCall } from '_lib/api/apiTypes.shim';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Box } from '_lib/components/Box/Box';
import { ErrorBoundaryFallback } from '_lib/components/ErrorBoundaryFallback/ErrorBoundaryFallback';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { Stack } from '_lib/components/Stack/Stack';
import { TabPanel } from '_lib/components/TabPanel/TabPanel';

import { ThreeTabFooter } from '_lib/featureComponents/ThreeTabFooter/ThreeTabFooter';
import { useMediaQuery } from '_lib/hooks/useMediaQuery';
import { useAppDispatch } from '_lib/redux/hooks';
import { VerticalDivider } from '_lib/styles/PhoneCall.styled';
import { PhoneCallURLState } from '_lib/types/PhoneCall.types';
import { logDebug } from '_lib/utils/logging';

import { StCapacityCompanyContext } from '../../common/CompanyPane/Company.slice';
import { CompanyPane } from '../../common/CompanyPane/CompanyPane';

import { CompanyZoneZipSelect } from './featureComponents/CompanyPane/CompanyZoneZipSelect/CompanyZoneZipSelect';
import { StCapacityCustomerPane } from './featureComponents/CustomerPane/StCapacityCustomerPane';
import { StCapacityCallDetails } from './featureComponents/StCapacityCallDetails/StCapacityCallDetails';
import { normalizeTradeFromCompany, switchDifferentTenantCompany } from './StCapacityPhoneCall.slice';

type StCapacityPhoneCallProps = {
  companyContext: StCapacityCompanyContext;
  phoneCallURLState: PhoneCallURLState;
  mappedCall: ServiceTitanMappedCall | null;
  handleSwitchCompany: (companyId: string) => Promise<Company | null>;
};

export const StCapacityPhoneCall = (props: StCapacityPhoneCallProps): React.ReactElement => {
  const { companyContext, phoneCallURLState, mappedCall, handleSwitchCompany } = props;

  const dispatch = useAppDispatch();

  const refForDialog = useRef(null);

  const handleSwitchStCapacityCompany = async (companyId: string) => {
    logDebug('// - Company Switch Handler ST Capacity - //');

    const newCompany = await handleSwitchCompany(companyId);
    if (!newCompany || !isStCapacityCompany(newCompany)) return;

    /*
     * NOTE: customerProviderId is not guaranteed to be Unique across companies think ST Instances with Integer IDs
     * So we should only let this cache live in the scope of a single company, with the exception of the case that
     * both companies have the same tenantId
     */
    if (
      newCompany.dataProvider.providerOptions.tenantId !== companyContext.company.dataProvider.providerOptions.tenantId
    ) {
      dispatch(switchDifferentTenantCompany(newCompany));
    } else {
      dispatch(normalizeTradeFromCompany(newCompany));
    }
  };

  const [tabIndex, setTabIndex] = useState(1); // default to customer pane shown if in tabbed view
  const [isSaving, setIsSaving] = useState(false);

  // If small screen, render with tabs - tradeoff to simplify added complexity of partial screen dialog
  const tabView = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  return (
    <Box sx={{ height: '100vh', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <BackdropSpinner open={isSaving} />
      <Stack direction={'row'} spacing={0} sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
        {/* NOTE - used to constrain getAvailability dialog */}
        <FlexBox
          sx={() => {
            if (tabView && tabIndex !== 2) {
              return { width: '100%', height: '100%', position: 'relative' };
            }
            if (tabView && tabIndex === 2) {
              return { width: '0%', height: '100%', position: 'relative' };
            }
            return { width: '60%', height: '100%', position: 'relative' };
          }}
          ref={refForDialog}>
          <TabPanel
            currentIndex={tabIndex}
            index={0}
            alwaysDisplay={!tabView}
            sx={() => {
              if (tabView && tabIndex === 0) {
                return { width: '100%', height: '100%', position: 'relative' };
              }
              if (tabView && tabIndex !== 0) {
                return { width: '0%', height: '100%', position: 'relative' };
              }
              return { width: '40%', height: '100%' };
            }}>
            <ErrorBoundary
              beforeCapture={(scope) => {
                scope.setTag('pane', 'St Capacity Call Details Pane - Outer');
              }}
              fallback={({ resetError }) => (
                <ErrorBoundaryFallback
                  title='Error Displaying St Capacity Call Details'
                  onRefresh={() => {
                    // NOTE - we do not have a way to 'rescue' state here without a serious refactor!
                    resetError();
                  }}
                />
              )}>
              <StCapacityCallDetails
                phoneCallURLState={phoneCallURLState}
                companyContext={companyContext}
                mappedCall={mappedCall}
                refForDialog={refForDialog}
                showFooter={true}
                onSetIsSaving={setIsSaving}
                sx={{ flexGrow: 1 }}
              />
            </ErrorBoundary>
          </TabPanel>
          <VerticalDivider />
          <TabPanel
            currentIndex={tabIndex}
            index={1}
            alwaysDisplay={!tabView}
            sx={() => {
              if (tabView && tabIndex === 1) {
                return { width: '100%', height: '100%', position: 'relative' };
              }
              if (tabView && tabIndex !== 1) {
                return { width: '0%', height: '100%', position: 'relative' };
              }
              return { width: '60%', height: '100%' };
            }}>
            <StCapacityCustomerPane
              phoneCallURLState={phoneCallURLState}
              company={companyContext.company}
              sx={{ flexGrow: 1 }}
            />
          </TabPanel>
        </FlexBox>
        <VerticalDivider />
        <TabPanel
          currentIndex={tabIndex}
          index={2}
          alwaysDisplay={!tabView}
          sx={() => {
            if (tabView && tabIndex === 2) {
              return { width: '100%', height: '100%', position: 'relative' };
            }
            if (tabView && tabIndex !== 2) {
              return { width: '0%', height: '100%', position: 'relative' };
            }
            return { width: '40%', height: '100%' };
          }}>
          <CompanyPane
            companyContext={companyContext}
            phoneCallURLState={phoneCallURLState}
            zipZoneOptions={{ ZipZoneSelectComponent: CompanyZoneZipSelect }}
            handleSwitchCompany={handleSwitchStCapacityCompany}
            sx={{ flexGrow: 1 }}
          />
        </TabPanel>
      </Stack>
      {tabView && <ThreeTabFooter tabIndex={tabIndex} setTabIndex={setTabIndex} />}
    </Box>
  );
};
